/**
 * O que a TELA entrega para baixo ao abrir voz em tempo real — [spec-008 T030c].
 *
 * Duas peças, e as duas faltavam por inteiro até aqui (`motor-de-voz.js`,
 * cabeçalho da [T030b]: *"também não existe ainda produtor da bandeira no
 * front"*):
 *
 * | peça | o que resolve |
 * |---|---|
 * | `montarContextoDeVozTempoReal` | o `ContextoDeSessao` de `contracts/transporte.md` — a `superficie` que `abrir` EXIGE e o caminho de `delegar` |
 * | `lerDesfechoDoTurnoFalado` | o que o `delegar` devolve para o laço da [T032]: `{texto, preview_id, valor_para_voz, tipo_do_valor}` lido do store do chat |
 *
 * ## ★ [T046] Não há bandeira aqui — o tempo real é o PADRÃO (D-O-4)
 *
 * Este arquivo LIA `FEATURES.IA_VOZ_TEMPO_REAL` do `$access` e o entregava
 * como `bandeiraTempoRealLigada`. **Saiu**: em Modo de Voz quem atende é o
 * transporte em tempo real, sem opt-in por escritório. O argumento que fechou
 * a questão veio do banco — a funcionalidade `'IA - Voz em Tempo Real'` nem
 * existe no `CHECK constraint` de `escritorio_funcionalidades`, então ligá-la
 * exigiria migration: não era gate, era obstáculo.
 *
 * **O que este arquivo produz agora são fatos, não opções:** a `superficie`
 * que a tela é e o `forcarPorTurnos` da queda ([T034]). Quem decide o
 * transporte a partir deles continua sendo o ponto ÚNICO de seleção
 * (`transporte-de-voz.js`, invariante N4) — aqui não há `if` de roteamento.
 *
 * ⚠️ **A `superficie` vazia NÃO é a bandeira com outro nome.** `abrir` RECUSA
 * sessão sem superfície (`MOTIVO_SEM_SUPERFICIE`), e o popup, o painel do Hub,
 * o Fee-Based e o ai-framework montam o MESMO composer sem declarar nenhuma.
 * Mandá-los ao tempo real gastaria uma queda e uma linha de aviso por clique
 * no microfone para terminar no MESMO caminho por turnos.
 *
 * ## `delegar` recebe um OBJETO, não uma string
 *
 * `contracts/transporte.md` diz `delegar: (texto) => AsyncIterable<string>`,
 * e a [T032] já reportou a divergência: string não carrega proposta, e o
 * `PedidoDeDelegacao` (C-6) tem seis campos. Quem chama é
 * `transporte-tempo-real-delegacao.js::_delegarAoChat`, com o pedido inteiro;
 * o que volta é drenado por `_drenar`, que aceita `AsyncIterable`, `Promise`
 * ou valor — e é de `{texto, preview_id}` que ele lê a proposta
 * (`lerPropostaDoResultado`). **A divergência segue reportada**; este lado
 * fala a forma que o consumidor real lê.
 *
 * ## ★ [T045] De onde sai o NÚMERO que a voz fala — D-O-3
 *
 * A [T044] entregou o formatador e a guarda, mas `valor_para_voz` não tinha
 * produtor: o desfecho caía sempre em `SAIDA_PRONTA` e a voz remetia sem dizer
 * número. **Este arquivo é o produtor**, e ele lê do store do chat a ÚNICA
 * forma de resultado da plataforma que é, por contrato, *um valor só com o
 * tipo dele declarado*: o cartão `card` do BI.
 *
 * | o quê | onde | fonte |
 * |---|---|---|
 * | um valor só | `parte.ui.props.type === 'card'` | `aawz_bi_v2/CLAUDE.md`: *"Card (`type: "card"`) — **Single value displays** with optional formatting"* |
 * | o número | `props.options.value` | `aawz_bi_v2/models/model_card.py::Card.new_model` (`value = self._data.get('value')`) |
 * | o TIPO | `props.options.format` | idem (`_format = self._options.get('format')`) — o mesmo vocabulário que a TELA honra (`TYPE_OPTIONS`, `@modules/dashboard/utils/FormatValues`) |
 *
 * ### As quatro recusas, e cada uma é o motivo de a guarda existir
 *
 * 1. **Vários valores ⇒ nenhum.** Dois cartões de valor único no mesmo turno,
 *    ou zero, não produzem nada: escolher "o primeiro" seria a voz decidindo
 *    qual dos fatos é O fato. Uma lista de posições e uma série temporal já
 *    caem fora antes disso, porque não são `type: 'card'`.
 * 2. **Sem `format`, sem valor.** `14.37` tanto é 14,37% quanto R$ 14,37, e
 *    adivinhar é exatamente o que a D-O-3 existe para impedir. Cartão de BI
 *    legado (sem `format`, com `value` já formatado em pt-BR) cai aqui.
 * 3. **Nunca do TEXTO.** Nada aqui olha a resposta do assistente. Pescar o
 *    número da prosa é a N21 ao contrário — a pista desambigua, nunca faz
 *    lookup —, e num parágrafo com três números não existe critério honesto
 *    para dizer qual é o fato.
 * 4. **Ausência é resultado.** Sem valor, as chaves nem entram no desfecho e a
 *    voz REMETE, que é o comportamento anterior à D-O-3 e é seguro.
 *
 * ⚠️ **Não existe `valor_para_voz` no envelope da tool, e não se inventa um.**
 * O nome é do contrato do `delegar` (consumido por `lerValorDoResultado`, em
 * `transporte-tempo-real-remissoes.js`); PRODUZI-lo a partir de um campo de
 * mesmo nome no `parte.value` seria declarar um contrato de backend que
 * ninguém escreveu — a terceira grafia antes da primeira, que é o erro que a
 * [T044] nomeou.
 */
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'
import {
  formatarParaFala,
  TIPO_MONETARIO,
  TIPO_NUMERO,
  TIPO_PERCENTUAL,
} from './formatar-valor-para-voz'

/**
 * A superfície da IA Operacional, a `/ia`.
 *
 * ⚠️ Uma constante, **não a lista**. O vocabulário fechado das três
 * superfícies é do servidor (`SUPERFICIES_CONHECIDAS`, com `choices` no
 * `reqparse`) e duplicá-lo aqui criaria duas listas divergindo em silêncio —
 * a mesma decisão já tomada em `apiVoz.abrirSessaoTempoReal`. O que o front
 * precisa é declarar QUAL superfície ele é, e isso é um literal por tela.
 *
 * O `/fee-fixo/chat` fica de fora porque já tem outra voz em tempo real
 * (Conversa Fluida, ElevenLabs). Declarar uma superfície nova é uma prop a
 * mais numa tela — revisável, e é assim que se pretende que seja: com a D-O-4
 * a superfície deixou de ser chave de rollout (D-9 revogada) e passou a ser o
 * que sempre foi no contrato, a declaração de QUE TELA está pedindo a sessão.
 */
export const SUPERFICIE_IA_OPERACIONAL = 'ia_operacional'

/**
 * O `ContextoDeSessao` (`contracts/transporte.md`) que a fachada repassa
 * INTEIRO aos dois transportes — cada um lê o que entende.
 *
 * @param {Object} opts
 * @param {string} opts.superficie `ia_operacional` | `consultor_estrategico` |
 *   `fee_fixo`. **Vocabulário FECHADO do servidor** e deliberadamente NÃO
 *   duplicado aqui (mesma decisão de `apiVoz.abrirSessaoTempoReal`): valor
 *   fora da lista volta 400 nomeado, melhor do que duas listas divergindo em
 *   silêncio. Vazio = a tela não declara superfície (popup, painel do Hub,
 *   Fee-Based, ai-framework) e o Modo de Voz dela segue pelo caminho por
 *   turnos, byte a byte como hoje.
 * @param {string} [opts.idConversa] conversa a que a sessão pertence.
 * @param {Function} [opts.delegar] o `POST /chat` de sempre — é por aqui que
 *   o cérebro e o RBAC ficam na AAWZ (D-4).
 * @param {boolean} [opts.forcarPorTurnos] a queda do [T034] reabrindo o v1 na
 *   MESMA sessão do consultor: sem isto, reabrir escolheria o tempo real de
 *   novo e o canal que acabou de cair seria tentado em laço. **Viaja no
 *   contexto**, e quem o lê é `selecionarTransporte`.
 * @param {string} [opts.nomeDoConsultor] ★ [T062] quem está do outro lado,
 *   como a SESSÃO o conhece — é com ele que a IA abre a conversa
 *   (`transporte-tempo-real.js::saudacaoDeAbertura`). Vem do usuário logado,
 *   **nunca do modelo**: o roteador do provedor altera identificador em 7 de
 *   8 medições (N21), e um nome errado na primeira frase queima a sessão
 *   inteira. Vazio ⇒ a saudação sai sem nome, que é o desfecho correto.
 * @param {Function} [opts.lerValorParcialDoTurno] ★ [T065] o valor que a
 *   consulta JÁ devolveu, lido AGORA — é ele que autoriza a voz a adiantar o
 *   número durante a espera (D-O-5). **Getter, não valor**: é chamado a cada
 *   acompanhamento, porque o cartão entra no store no meio do turno e um
 *   valor congelado na abertura seria sempre `null`. Ausente ⇒ a voz remete,
 *   que é o comportamento anterior à D-O-5 e é seguro.
 * @returns {Object} `{superficie, idConversa, delegar, forcarPorTurnos,
 *   nomeDoConsultor, lerValorParcialDoTurno}`
 */
export const montarContextoDeVozTempoReal = ({
  superficie,
  idConversa,
  delegar,
  forcarPorTurnos = false,
  nomeDoConsultor = '',
  lerValorParcialDoTurno,
} = {}) => ({
  superficie: String(superficie || '').trim(),
  idConversa: idConversa || null,
  delegar: typeof delegar === 'function' ? delegar : null,
  forcarPorTurnos: Boolean(forcarPorTurnos),
  nomeDoConsultor: String(nomeDoConsultor || '').trim(),
  // Mesma régua de `delegar`: o que não é função vira `null`, e quem lê lá
  // embaixo (`transporte-tempo-real-delegacao.js::_valorParcialDoTurno`) já
  // trata a ausência devolvendo `null` — a voz diz o ESTADO.
  lerValorParcialDoTurno:
    typeof lerValorParcialDoTurno === 'function' ? lerValorParcialDoTurno : null,
})

/**
 * ★ O que conta como RESPOSTA — uma régua só, para dois leitores.
 *
 * `_ultimaResposta` (o texto que vira desfecho) e
 * `contarRespostasDoAssistente` (o que amarra o desfecho ao turno) têm de
 * olhar EXATAMENTE o mesmo conjunto: é essa igualdade que faz *"o conjunto
 * não cresceu"* significar *"o elemento que `_ultimaResposta` devolve é o
 * mesmo de antes do envio"*. Duas regras separadas divergiriam no primeiro
 * `role` novo, e o sintoma seria a voz afirmando o dado da pergunta ANTERIOR
 * — em silêncio, que é o defeito que a amarração veio fechar.
 */
const _ehResposta = (msg) => Boolean(msg) && msg.role === 'assistant'

const _ultimaResposta = (mensagens) => {
  const lista = Array.isArray(mensagens) ? mensagens : []
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    if (_ehResposta(lista[i])) return String(lista[i].content || '')
  }
  return ''
}

/**
 * Quantas respostas do assistente a conversa tem AGORA — o discriminante que
 * amarra o desfecho ao turno que acabou de ser falado.
 *
 * ★ **Por que ele discrimina:** `_ultimaResposta` devolve o ÚLTIMO elemento
 * deste mesmo conjunto. Se o conjunto não cresceu entre o instante ANTES do
 * envio e o instante DEPOIS dele, o elemento que ela devolveria já estava lá
 * antes — ou seja, é a resposta de OUTRA pergunta. Contar o conjunto INTEIRO
 * de mensagens não serviria: o próprio envio acrescenta a bolha do usuário,
 * então o total muda sempre, inclusive quando o turno morre sem responder.
 *
 * ⚠️ A contagem não distingue uma lista RECARREGADA do zero com o mesmo
 * número de respostas (troca de conversa no meio do turno falado, que a view
 * já descarta por outro caminho). O erro possível aí é o mesmo de qualquer
 * discriminante por identidade, e o lado seguro continua sendo o desta
 * função: na dúvida, "não cresceu" ⇒ a voz diz que não conseguiu.
 *
 * @param {Array} mensagens o `messages` do store do chat.
 * @returns {number}
 */
export const contarRespostasDoAssistente = (mensagens) => {
  const lista = Array.isArray(mensagens) ? mensagens : []
  let total = 0
  for (let i = 0; i < lista.length; i += 1) {
    if (_ehResposta(lista[i])) total += 1
  }
  return total
}

/**
 * ★ [spec-008 T077] A run em voo deu SINAL DE VIDA entre duas olhadas?
 *
 * É o discriminante que separa "o agente está trabalhando" de "o agente
 * morreu", e existe porque o relógio sozinho não separa: medido em
 * 19/09/2026, uma run respondeu aos **368 s** e outra FALHOU aos **581 s** —
 * a diferença entre as duas não está na duração, está em continuar ou não
 * publicando evento (ver `MS_SEM_PROGRESSO_DA_RUN` em `view-chat.vue`).
 *
 * Três formas de avanço, e as duas primeiras importam tanto quanto a óbvia:
 *
 * 1. **A run APARECEU** (`antes` nulo, `agora` não). O `POST /runs` responde em
 *    ~400 ms, então a espera quase sempre começa antes de haver cursor algum.
 * 2. **A run é OUTRA** (`id_run` mudou). O cursor RECOMEÇA do zero a cada run
 *    (`_reiniciarAcompanhamentoDeRun` zera `_runAtualSeq`), então comparar só
 *    o número trataria o run novo como estagnado — e a espera morreria de
 *    "silêncio" exatamente no turno que acabou de começar.
 * 3. **O cursor andou** (`seq` maior).
 *
 * ⚠️ `agora` nulo NÃO é avanço nem é regressão: é a run tendo sido encerrada
 * (`resetRunAtual`) com a mensagem ainda a caminho. O relógio de silêncio
 * segue correndo de onde estava, que é o comportamento certo — se a mensagem
 * não chegar, a espera fecha pelo teto.
 *
 * @param {?{idRun: ?string, seq: number}} antes
 * @param {?{idRun: ?string, seq: number}} agora
 * @returns {boolean}
 */
export const houveAvancoDaRun = (antes, agora) => {
  if (!agora) return false
  if (!antes) return true
  if (antes.idRun !== agora.idRun) return true
  return agora.seq > antes.seq
}

/**
 * A proposta que o turno deixou esperando decisão NA TELA.
 *
 * O discriminante é o ESTADO da parte (`approval-requested`), não a mera
 * presença de `preview_id`: uma parte já resolvida (`output-available`)
 * também o carrega, e remeter ao cartão de uma escrita que já aconteceu é
 * dizer ao consultor que falta decidir algo que ninguém vai decidir.
 */
const _propostaPendente = (partesDeTool) => {
  const lista = Array.isArray(partesDeTool) ? partesDeTool : []
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    const parte = lista[i]
    if (!parte || parte.state !== TOOL_PART_STATE.APROVACAO) continue
    if (parte.preview_id) return String(parte.preview_id)
  }
  return ''
}

/**
 * ★ [T045] O tipo de componente do dashboard que é, por contrato, **um valor
 * só** — e o ÚNICO discriminante deste produtor.
 *
 * `aawz_bi_v2/CLAUDE.md`: *"Card (`type: "card"`) — Single value displays with
 * optional formatting"*. Os outros 18 tipos do enum (`bars`, `table`, `donut`,
 * `goal`, `ranking-*`, …) desenham série, lista ou razão — vários números.
 */
export const TIPO_DE_COMPONENTE_DE_VALOR_UNICO = 'card'

/**
 * ★ [T045] `options.format` do cartão → o `tipo` que `formatarParaFala` aceita.
 *
 * O vocabulário é o da TELA (`TYPE_OPTIONS`, `@modules/dashboard/utils/
 * FormatValues`) — o mesmo que o `type-card.vue` honra ao desenhar o número.
 * Há junção com ele no teste: mapear um formato que a tela não conhece faria a
 * voz falar em unidade que o cartão não mostra.
 *
 * ⚠️ **`porcentagem_decimal` fica DE FORA de propósito**, embora exista no
 * `TYPE_OPTIONS`: ali o número é FRAÇÃO (`toDecPercent` faz `v * 100`, então
 * `0,1` é 10%). Falá-lo como percentual diria *"0,1%"* — erro de duas ordens
 * de grandeza —, e convertê-lo aqui faria a voz dizer um número que a tool não
 * devolveu, quebrando o invariante que `conferirQueRemete` confere. Também
 * ficam fora `cnpj` (não é grandeza) e todo formato desconhecido.
 */
export const FORMATO_PARA_TIPO_DO_VALOR = Object.freeze({
  monetario: TIPO_MONETARIO,
  monetario_abreviado: TIPO_MONETARIO,
  R$: TIPO_MONETARIO,
  porcentagem: TIPO_PERCENTUAL,
  '%': TIPO_PERCENTUAL,
  numero: TIPO_NUMERO,
})

/**
 * O tipo que o FORMATO declara, ou `''`.
 *
 * ⚠️ `hasOwnProperty` e não `mapa[chave]`: `format: 'toString'` devolveria uma
 * função do protótipo — truthy —, e a voz falaria com um "tipo" que é código.
 */
const _tipoDoFormato = (formato) => {
  if (typeof formato !== 'string' || !formato) return ''
  if (!Object.prototype.hasOwnProperty.call(FORMATO_PARA_TIPO_DO_VALOR, formato)) {
    return ''
  }
  return FORMATO_PARA_TIPO_DO_VALOR[formato]
}

/**
 * O valor único de UMA parte, ou `null`.
 *
 * ⚠️ Só parte RESOLVIDA (`output-available`). A que ainda roda não tem fato; a
 * recusada e a que falhou têm o oposto de um.
 *
 * ⚠️ A última guarda é o PRÓPRIO formatador: emitir `valor_para_voz` que ele
 * não consegue falar (string em pt-BR, `null`, `NaN`) faria o desfecho anunciar
 * um valor que o caminho abaixo descarta — produtor e consumidor precisam
 * concordar sobre o que é número legível, e o jeito de garantir isso é ter uma
 * régua só.
 */
const _valorDoCartaoDeValorUnico = (parte) => {
  if (!parte || parte.state !== TOOL_PART_STATE.SAIDA) return null
  const ui = parte.ui
  if (!ui) return null
  // ⚠️ Sem conferir `ui.component`: `type: 'card'` JÁ é o discriminante. O
  // `if` do componente existiu e foi RETIRADO por ser inalcançável — nenhum
  // outro schema do registry admite `card` no enum de `type` (varredura em
  // `contexto-de-voz-tempo-real.unit.js`), então ele não tinha como ficar
  // vermelho, e guarda que nenhum teste derruba é decoração. Quem protege o
  // dia em que um segundo cartão admitir `card` é a varredura.
  const props = ui.props
  if (!props || props.type !== TIPO_DE_COMPONENTE_DE_VALOR_UNICO) return null
  const opcoes = props.options
  if (!opcoes) return null
  const tipo = _tipoDoFormato(opcoes.format)
  if (!tipo) return null
  if (!formatarParaFala(opcoes.value, { tipo })) return null
  return { valor: opcoes.value, tipo }
}

/**
 * ★ [T045] O valor do TURNO — e só quando o turno inteiro tem UM.
 *
 * Duas partes com valor único devolvem `null`: dizer o de uma delas é escolher
 * por conta própria qual dos dois fatos o consultor queria ouvir, e é o mesmo
 * erro de falar "o primeiro" de uma lista.
 */
const _valorDoTurno = (partesDeTool) => {
  const lista = Array.isArray(partesDeTool) ? partesDeTool : []
  let achado = null
  for (let i = 0; i < lista.length; i += 1) {
    const candidato = _valorDoCartaoDeValorUnico(lista[i])
    if (!candidato) continue
    if (achado) return null
    achado = candidato
  }
  return achado
}

/**
 * ★ O desfecho de um turno falado que NÃO produziu resposta — o turno que
 * não chegou a sair (havia outro em voo) e o que saiu e morreu sem responder.
 *
 * ⚠️ **Não é copy nova, e não pode ser.** O que o consultor OUVE é a
 * `instrucao_de_fala` de `SAIDA_DE_FALHA`
 * (`transporte-tempo-real-remissoes.js`), já congelada e já em PT-BR: *"a
 * consulta não pôde ser concluída agora... peça para ele tentar de novo em
 * instantes. NÃO invente nenhum dado"*. Quem escolhe essa saída é
 * `_saidaDoDesfecho`, e o critério dele é exatamente `texto` vazio sem
 * proposta e sem valor — que é este objeto. Escrever aqui uma frase própria
 * seria inócuo (`texto` só é lido por verdade/falsidade) ou pior: uma saída
 * fora da lista branca de `conferirQueRemete`, recusada no ponto de envio.
 *
 * ⚠️ **O motivo técnico nunca viaja** (N7): "havia um turno em voo" é
 * diálogo de engenharia, e a voz não o diz — ela pede para repetir.
 */
export const DESFECHO_SEM_RESPOSTA = Object.freeze({ texto: '' })

/**
 * O desfecho do turno delegado, na forma que `transporte-tempo-real-
 * delegacao.js::_desfechoDeUmValor` lê.
 *
 * ⚠️ **`preview_id` só entra quando existe** — `lerPropostaDoResultado`
 * considera proposta qualquer valor truthy nessa chave, e mandar `''`/`null`
 * explicitamente é inofensivo, mas mandar a chave com um id de um turno
 * ANTERIOR faria a voz remeter a um cartão que o consultor já fechou.
 *
 * ⚠️ **`valor_para_voz`/`tipo_do_valor` seguem a MESMA regra, e por um motivo
 * mais duro:** `lerValorDoResultado` só devolve `null` com a chave ausente ou
 * nula, e `conferirQueRemete` trata *"valor falado sem origem"* como vermelho.
 * As duas chaves andam em par — tipo sem valor não fala nada, valor sem tipo é
 * o palpite que a D-O-3 proíbe.
 *
 * @param {Object} opts
 * @param {Array} opts.mensagens o `messages` do store do chat.
 * @param {Array} opts.partesDeTool o `toolPartsOrdenadas` do store.
 * @returns {{texto: string, preview_id?: string, valor_para_voz?: *, tipo_do_valor?: string}}
 */
export const lerDesfechoDoTurnoFalado = ({ mensagens, partesDeTool } = {}) => {
  const desfecho = { texto: _ultimaResposta(mensagens) }
  const proposta = _propostaPendente(partesDeTool)
  if (proposta) desfecho.preview_id = proposta
  const valor = _valorDoTurno(partesDeTool)
  if (valor) {
    desfecho.valor_para_voz = valor.valor
    desfecho.tipo_do_valor = valor.tipo
  }
  return desfecho
}

/**
 * ★ [T065] O MESMO valor, lido **enquanto o turno ainda corre** — D-O-5.
 *
 * Palavras do owner (17/09/2026): *"pode até adiantar número se já tiver isso
 * (ou seja, se a resposta já estiver nas mãos do agente)"*. O instante existe
 * e é medível: o cartão entra no store quando a TOOL dele fecha, e o turno só
 * termina depois — havia uma janela em que o número já estava na tela do
 * consultor e a voz ainda dizia "estou consultando".
 *
 * ## Por que é uma função separada, e não `lerDesfechoDoTurnoFalado`
 *
 * Não é o mesmo fato. O desfecho responde *"o turno acabou, e o que ele
 * deixou?"* — por isso carrega `texto` (a resposta do assistente, que **não
 * existe** no meio do turno) e `preview_id` (a decisão esperando na tela, que
 * é assunto do FIM). Este responde *"tem número em mãos AGORA?"*. Reusar o
 * desfecho aqui faria a voz adiantar um `texto` vazio como se fosse resultado
 * e remeter a uma proposta que o consultor ainda não pode decidir.
 *
 * ⚠️ **O discriminante é o de sempre, sem exceção para o meio do turno**: só
 * parte RESOLVIDA (`output-available`), só cartão de valor ÚNICO, só com
 * `format` declarado, e só se o próprio `formatarParaFala` souber falá-lo —
 * `_valorDoCartaoDeValorUnico` é quem decide, e é a MESMA régua do desfecho.
 * Dois cartões de valor único no mesmo turno continuam devolvendo `null`:
 * adiantar "o primeiro" seria a voz escolhendo qual dos fatos é O fato.
 *
 * ⚠️ **Ausência é resultado válido.** `null` ⇒ a voz diz o ESTADO, como antes
 * da D-O-5. É isso que impede inventar número quando o dado não veio — e é
 * por isso que a degradação daqui é silenciosa de propósito.
 *
 * @param {Object} opts
 * @param {Array} opts.partesDeTool o `toolPartsOrdenadas` do store. ⚠️ Ele é
 *   COMPOSTO no namespace RAIZ (`store/index.js` faz `{...getters,
 *   ...toolPartsGetters}`): `${STORE}/toolParts/toolPartsOrdenadas` devolve
 *   `undefined` em SILÊNCIO, e a voz voltaria a nunca adiantar número.
 * @returns {?{valor_para_voz: *, tipo_do_valor: string}} na MESMA forma que
 *   `transporte-tempo-real-remissoes.js::lerValorDoResultado` lê — as duas
 *   chaves andam em par, porque tipo sem valor não fala nada e valor sem tipo
 *   é o palpite que a D-O-3 proíbe.
 */
export const lerValorParcialDoTurnoFalado = ({ partesDeTool } = {}) => {
  const valor = _valorDoTurno(partesDeTool)
  if (!valor) return null
  return { valor_para_voz: valor.valor, tipo_do_valor: valor.tipo }
}
